import React, { useEffect, useMemo, useState } from 'react';
import {
  Link, NavLink, useNavigate, useLocation,
} from 'react-router-dom';
import {
  Navbar, Container, Offcanvas, Form, InputGroup, Button,
} from 'react-bootstrap';
import { logout } from '../Controllers/AuthController';
import useKeyboardShortcuts, { getModifierKey } from '../Hooks/useKeyboardShortcuts';
import useNotifications from '../Hooks/useNotifications';
import useFrequentPaths, { recordPageVisit } from '../Hooks/useRecentPages';
import useTripClaimsSummary from '../Hooks/useTripClaimsSummary';
import { TRIP_CLAIMS_PATH } from '../Utils/tripClaims';
import { getAccessibleSections, findNavItem } from '../config/navigation';
import NotificationBell from './NotificationBell';
import NotificationToast from './NotificationToast';
import CommandPalette, { OPEN_PALETTE_EVENT } from './CommandPalette';

const COLLAPSE_STORAGE_KEY = 'ph_nav_collapsed';

// Helper to render hotkey badge - Desktop only
function HotkeyBadge({ shortcutKey, modifierKey }) {
  return (
    <span className="hotkey-badge ms-auto d-none d-lg-inline">
      {modifierKey}
      +
      {shortcutKey.toUpperCase()}
    </span>
  );
}

// A single nav link rendered from a nav-config item — shared by the main menu,
// the grouped sections, and the "Frequent" shortcut row so there is one markup.
function MenuNavItem({
  item, modifierKey, onClick, badges = {},
}) {
  const count = item.badge ? badges[item.badge] : 0;
  return (
    <NavLink to={item.path} className="menu-nav-item" onClick={onClick}>
      <span className="menu-nav-icon" style={{ background: item.bg, color: item.color }}>
        <i className={`fa ${item.icon}`} />
      </span>
      <span className="menu-nav-label">{item.label}</span>
      {count > 0 && (
        <span className="badge rounded-pill text-bg-warning ms-2" data-testid={`nav-badge-${item.badge}`}>{count}</span>
      )}
      {item.shortcut && <HotkeyBadge shortcutKey={item.shortcut} modifierKey={modifierKey} />}
    </NavLink>
  );
}

function Header({ isAuthed, session }) {
  const [isLoading, setIsLoading] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return JSON.parse(window.localStorage.getItem(COLLAPSE_STORAGE_KEY)) || {};
    } catch (e) {
      return {};
    }
  });

  const navigate = useNavigate();
  const location = useLocation();

  const handleClose = () => setShowMenu(false);
  const handleShow = () => setShowMenu(true);

  // Enable keyboard shortcuts
  useKeyboardShortcuts(isAuthed);

  // Record page visits to power the "Frequent" section + palette ordering.
  useEffect(() => {
    if (isAuthed) recordPageVisit(location.pathname);
  }, [isAuthed, location.pathname]);

  // Notifications hook
  const {
    notifications,
    unreadCount,
    isLoading: notificationsLoading,
    hasMore,
    newNotification,
    fetchMore,
    refresh: refreshNotifications,
    markAsRead,
    markAllAsRead,
    clearNewNotification,
  } = useNotifications(isAuthed);

  const modifierKey = getModifierKey();

  // Trip claims waiting for review — on the menu entry, and next to the bell
  // so it is seen from anywhere without opening the menu.
  const tripClaims = useTripClaimsSummary(isAuthed);
  const navBadges = { tripClaims: tripClaims.total };

  // Nav data, derived from the single config (permission-filtered).
  const sections = useMemo(() => getAccessibleSections(), [isAuthed]);
  const frequentPaths = useFrequentPaths(4);
  const frequentItems = useMemo(() => {
    // Any page this user can reach. Frequent is a shortcut list built from
    // what they actually open, so a page can appear here AND in its own
    // group — that is what a shortcut is, not a duplicate to design away.
    const eligiblePaths = new Set(
      sections.flatMap((s) => s.items.map((i) => i.path)),
    );
    return frequentPaths
      .filter((p) => eligiblePaths.has(p))
      .map(findNavItem)
      .filter(Boolean);
  }, [frequentPaths, sections]);

  const toggleSection = (id, currentlyCollapsed) => {
    setCollapsed((prev) => {
      // Stored against what is on SCREEN, not against the absence of a
      // stored value: a section that starts open has nothing stored, and
      // toggling it has to close it rather than "expand" what is already open.
      const next = { ...prev, [id]: !currentlyCollapsed };
      try {
        window.localStorage.setItem(COLLAPSE_STORAGE_KEY, JSON.stringify(next));
      } catch (e) {
        // ignore — collapse state persistence is non-essential
      }
      return next;
    });
  };

  const openPalette = () => {
    handleClose();
    window.dispatchEvent(new Event(OPEN_PALETTE_EVENT));
  };

  const onLogout = async () => {
    setIsLoading(true);
    const logoutAction = await logout();

    if (logoutAction) {
      setIsLoading(false);
      // eslint-disable-next-line no-restricted-globals
      location.reload();
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const q = searchQuery.trim();
    if (!q) return;
    navigate(`/orders?quick_search=${encodeURIComponent(q)}`);
    setSearchQuery('');
  };

  return (
    <Navbar variant="dark" className="modern-navbar">
      <Container fluid className="container-custom">
        {/* Brand */}
        {/* `to`, not `href`: an href here made the browser fetch the whole
            app again — a white flash and a full reload just to go Home. */}
        <Navbar.Brand as={Link} to="/home" className="brand-logo">
          <img
            src="/PickupHungary_Primary_Color.svg"
            alt="Pick-up Hungary"
            className="brand-img"
          />
        </Navbar.Brand>

        {/* Center — search (desktop only) */}
        {isAuthed && (
          <Form onSubmit={handleSearch} className="header-search d-none d-md-flex mx-3">
            <InputGroup size="sm">
              <InputGroup.Text className="header-search-icon">
                <i className="fa fa-search" />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Search bookings..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="header-search-input"
              />
            </InputGroup>
          </Form>
        )}

        {/* Right side */}
        <div className="d-flex align-items-center ms-auto gap-2">
          {/* New Order button */}
          {isAuthed && (
            <Button
              size="sm"
              variant="primary"
              className="header-new-order"
              onClick={() => navigate('/orders?new=true')}
            >
              <i className="fa fa-plus me-1" />
              <span className="d-none d-sm-inline">New Order</span>
            </Button>
          )}

          {/* User greeting - desktop */}
          {isAuthed && (
            <div className="user-greeting d-none d-lg-flex">
              <div className="user-avatar">
                {session.givenName?.[0]?.toUpperCase()}
              </div>
              <div className="greeting-text">
                <small>Signed in as</small>
                <strong>{session.givenName}</strong>
              </div>
            </div>
          )}

          {/* Trip claims waiting for review */}
          {isAuthed && tripClaims.total > 0 && (
            <NavLink
              to={TRIP_CLAIMS_PATH}
              className="btn btn-sm btn-warning d-inline-flex align-items-center"
              title={`Trip Claims: ${tripClaims.total} waiting for review`}
              aria-label={`Trip Claims: ${tripClaims.total} waiting for review`}
              data-testid="header-trip-claims"
            >
              <i className="fa fa-receipt" />
              <span className="d-none d-sm-inline ms-1">Trip Claims</span>
              <span className="badge rounded-pill text-bg-dark ms-1">{tripClaims.total}</span>
            </NavLink>
          )}

          {/* Notification Bell */}
          {isAuthed && (
            <NotificationBell
              notifications={notifications}
              unreadCount={unreadCount}
              isLoading={notificationsLoading}
              hasMore={hasMore}
              onFetchMore={fetchMore}
              onRefresh={refreshNotifications}
              onMarkAsRead={markAsRead}
              onMarkAllAsRead={markAllAsRead}
            />
          )}

          {!isAuthed && (
            <a href="/login" className="btn btn-login me-3">
              <i className="fa fa-user me-2" />
              Login
            </a>
          )}

          {/* Menu Button */}
          <button
            className="navbar-toggler modern-toggler"
            type="button"
            onClick={handleShow}
            aria-label="Toggle navigation"
          >
            <span className="navbar-toggler-icon" />
          </button>
        </div>

        {/* Toast notification */}
        {isAuthed && (
          <NotificationToast
            notification={newNotification}
            onClose={clearNewNotification}
          />
        )}

        {/* Command palette (Cmd/Ctrl+K) */}
        {isAuthed && <CommandPalette />}

        {/* Offcanvas Menu */}
        <Offcanvas
          show={showMenu}
          onHide={handleClose}
          placement="end"
          className="offcanvas-menu"
        >
          <Offcanvas.Header closeButton className="offcanvas-menu-header">
            <img
              src="/PickupHungary_Primary_Color.svg"
              alt="Pick-up Hungary"
              style={{ height: '22px' }}
            />
          </Offcanvas.Header>
          <Offcanvas.Body className="offcanvas-menu-body">
            {isAuthed && (
              <>
                {/* User card */}
                <div className="menu-user-card">
                  <div className="menu-user-avatar">
                    {session.givenName?.[0]?.toUpperCase()}
                  </div>
                  <div>
                    <div className="menu-user-name">{session.givenName}</div>
                    <div className="menu-user-label">Signed in</div>
                  </div>
                </div>

                {/* Quick access splits by breakpoint so the two never collide:
                    mobile gets the prominent bookings search (desktop has that
                    in the header), desktop gets the page-jump palette trigger. */}
                <Form onSubmit={handleSearch} className="menu-search d-md-none">
                  <InputGroup>
                    <InputGroup.Text className="menu-search-icon">
                      <i className="fa fa-search" />
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      placeholder="Search bookings…"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="menu-search-input"
                    />
                  </InputGroup>
                </Form>

                <button
                  type="button"
                  className="menu-command-trigger d-none d-md-flex"
                  onClick={openPalette}
                >
                  <i className="fa fa-compass me-2" />
                  <span>Jump to a page…</span>
                  <span className="menu-command-kbd">
                    {modifierKey}
                    +K
                  </span>
                </button>

                {/* Frequent pages */}
                {frequentItems.length > 0 && (
                  <>
                    <div className="menu-section-label">Frequent</div>
                    <nav className="menu-nav">
                      {frequentItems.map((item) => (
                        <MenuNavItem
                          key={`freq-${item.path}`}
                          item={item}
                          modifierKey={modifierKey}
                          onClick={handleClose}
                        />
                      ))}
                    </nav>
                  </>
                )}

                {/* Grouped navigation, rendered from config */}
                {sections.map((section) => {
                  const isCollapsible = Boolean(section.label);
                  // The user's own choice wins and is remembered; until they
                  // make one, a section starts as its `defaultOpen` says. Every
                  // section carries a label now, so "has a label" can no longer
                  // stand in for "this is the primary one, keep it open".
                  const chosen = collapsed[section.id];
                  const isCollapsed = isCollapsible
                    && (chosen === undefined ? !section.defaultOpen : chosen !== false);
                  return (
                    <React.Fragment key={section.id}>
                      {isCollapsible && (
                        <button
                          type="button"
                          className="menu-section-toggle"
                          onClick={() => toggleSection(section.id, isCollapsed)}
                          aria-expanded={!isCollapsed}
                        >
                          <span className="menu-section-label">{section.label}</span>
                          <i className={`fa fa-chevron-${isCollapsed ? 'right' : 'down'} menu-section-chevron`} />
                        </button>
                      )}
                      {!isCollapsed && (
                        <nav className="menu-nav">
                          {section.items.map((item) => (
                            <MenuNavItem
                              key={item.path}
                              item={item}
                              modifierKey={modifierKey}
                              onClick={handleClose}
                              badges={navBadges}
                            />
                          ))}
                        </nav>
                      )}
                    </React.Fragment>
                  );
                })}

                {/* Keyboard shortcuts - desktop */}
                <div className="menu-shortcuts d-none d-lg-flex">
                  <i className="fa fa-keyboard me-2" />
                  <span>
                    Press
                    {' '}
                    <kbd>{modifierKey}</kbd>
                    {' '}
                    + key to navigate
                  </span>
                </div>

                <div className="menu-footer">
                  <button
                    type="button"
                    onClick={() => { handleClose(); onLogout(); }}
                    className="menu-logout-btn"
                  >
                    {!isLoading ? (
                      <>
                        <i className="fa fa-sign-out-alt me-2" />
                        Sign out
                      </>
                    ) : (
                      <i className="fa fa-spin fa-spinner" />
                    )}
                  </button>
                </div>
              </>
            )}

            {!isAuthed && (
              <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '200px' }}>
                <a href="/login" className="btn btn-primary">
                  <i className="fa fa-user me-2" />
                  Login
                </a>
              </div>
            )}
          </Offcanvas.Body>
        </Offcanvas>
      </Container>
    </Navbar>
  );
}

export default Header;
